import { drawingPoints } from "../ink";
import { InkStroke } from "./InkStroke";
import { lazy, Suspense } from "react";
import "@note-app/typography/styles.css";
const DiagramThumbnail = lazy(() => import("@note-app/diagrams/thumbnail").then((module) => ({ default: module.DiagramThumbnail })));
import { canvasTextStyle } from "../node-style";
import type { CanvasNode, JsonValue } from "../domain/types";
import type { CanvasAssetResolver, ReferenceResolution } from "../references";
import { useAssetUrl } from "./hooks";
import { useCanvasMessages } from "./i18n";

export function canvasStringProp(
  props: Record<string, JsonValue>,
  key: string,
  fallback: string,
): string {
  return typeof props[key] === "string" ? props[key] : fallback;
}

export function canvasNumberProp(
  props: Record<string, JsonValue>,
  key: string,
  fallback: number,
): number {
  const value = props[key];
  return typeof value === "number" ? value : fallback;
}

export interface CanvasNodeContentProps {
  node: CanvasNode;
  selected: boolean;
  referenceResolution?: ReferenceResolution | undefined;
  referencePending: boolean;
  referenceResolverAvailable: boolean;
  assetResolver?: CanvasAssetResolver | undefined;
  onReferenceActivate?: ((node: CanvasNode) => void) | undefined;
}

export function CanvasNodeContent({
  node,
  referenceResolution,
  referencePending,
  referenceResolverAvailable,
  assetResolver,
  onReferenceActivate,
}: CanvasNodeContentProps) {
  const messages = useCanvasMessages();
  const { x, y, width, height } = node.geometry;
  const fill = canvasStringProp(
    node.props,
    "fill",
    node.type === "sticky" ? "#fff2a8" : "#ffffff",
  );
  const stroke = canvasStringProp(node.props, "stroke", "#64748b");
  const strokeWidth = canvasNumberProp(node.props, "strokeWidth", 1.5);
  const textColor = canvasStringProp(node.props, "textColor", "#172033");
  const fontSize = canvasNumberProp(node.props, "fontSize", 16);
  const fontFamily = canvasStringProp(node.props, "fontFamily", "system-ui, sans-serif");
  const radius = canvasNumberProp(node.props, "radius", 10);
  const attachmentId =
    node.type === "image"
      ? canvasStringProp(node.props, "attachmentId", "")
      : undefined;
  const assetUrl = useAssetUrl(
    attachmentId === "" ? undefined : attachmentId,
    assetResolver,
  );
  const commonRect = {
    fill,
    stroke,
    strokeWidth,
    strokeDasharray: node.props.strokeStyle === "dashed" ? "7 6" : undefined,
    vectorEffect: "non-scaling-stroke" as const,
  };

  switch (node.type) {
    case "diagram":
      return <><rect x={x} y={y} width={width} height={height} rx={10} {...commonRect} />
        <text x={x + 14} y={y + 24} fill={textColor} fontSize={14} fontWeight={600}>{canvasStringProp(node.props, "title", "软件工程图")}</text>
        <foreignObject x={x + 12} y={y + 36} width={Math.max(1, width - 24)} height={Math.max(1, height - 48)} style={{ pointerEvents: "none" }}>
          <Suspense fallback={<div>正在加载图表…</div>}><DiagramThumbnail source={canvasStringProp(node.props, "source", "")} title={canvasStringProp(node.props, "title", "软件工程图")} /></Suspense>
        </foreignObject></>;
    case "frame":
      return (
        <>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={radius}
            {...commonRect}
            fillOpacity={0.08}
            strokeDasharray="8 6"
          />
          <text
            x={x + 12}
            y={y + 24}
            fill={textColor}
            fontSize={14}
            fontWeight={650}
          >
            {canvasStringProp(node.props, "title", messages.defaultFrameTitle)}
          </text>
        </>
      );
    case "shape": {
      const shape = canvasStringProp(node.props, "shape", "rectangle");
      const graphic =
        shape === "ellipse" ? (
          <ellipse
            cx={x + width / 2}
            cy={y + height / 2}
            rx={width / 2}
            ry={height / 2}
            {...commonRect}
          />
        ) : shape === "diamond" ? (
          <polygon
            points={`${x + width / 2},${y} ${x + width},${y + height / 2} ${x + width / 2},${y + height} ${x},${y + height / 2}`}
            {...commonRect}
          />
        ) : (
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={radius}
            {...commonRect}
          />
        );
      return (
        <>
          {graphic}
          <foreignObject
            x={x + 12}
            y={y + 12}
            width={width - 24}
            height={height - 24}
          >
            <div
              style={{
                display: "grid",
                alignItems: "center",
                width: "100%",
                height: "100%",
                color: textColor,
                fontSize, lineHeight: 1.4, fontFamily,
                overflow: "hidden",
                ...canvasTextStyle(node),
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {canvasStringProp(node.props, "label", "")}
            </div>
          </foreignObject>
        </>
      );
    }
    case "drawing":
      return <InkStroke points={drawingPoints(node)} color={stroke} width={canvasNumberProp(node.props, "strokeWidth", 3)} interactive />;
    case "image":
      return (
        <>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={8}
            {...commonRect}
            fill="#e2e8f0"
          />
          {typeof assetUrl === "string" ? (
            <image
              href={assetUrl}
              x={x}
              y={y}
              width={width}
              height={height}
              preserveAspectRatio={
                node.props.fit === "cover" ? "xMidYMid slice" : "xMidYMid meet"
              }
              role="img"
              aria-label={canvasStringProp(
                node.props,
                "alt",
                messages.nodeLabel("image"),
              )}
            />
          ) : (
            <text
              x={x + width / 2}
              y={y + height / 2}
              textAnchor="middle"
              fill="#64748b"
              fontSize={14}
            >
              {attachmentId === ""
                ? messages.missingImageAttachment
                : assetResolver === undefined
                  ? messages.imageResolverUnavailable
                  : assetUrl === null
                    ? messages.unavailableImage
                    : messages.loadingImage}
            </text>
          )}
        </>
      );
    case "reference": {
      const targetLabel =
        node.reference === undefined
          ? messages.missingReference
          : `${node.reference.kind}:${node.reference.id}`;
      const title =
        node.reference !== undefined && !referenceResolverAvailable
          ? messages.referenceResolverUnavailable
          : referencePending
            ? messages.loadingReference
            : referenceResolution?.status === "missing" &&
                referenceResolution.title.length === 0
              ? messages.unavailableReference
              : (referenceResolution?.title ?? targetLabel);
      const summary = !referenceResolverAvailable
        ? targetLabel
        : referenceResolution?.status === "resolved"
          ? referenceResolution.summary
          : referenceResolution?.reason;
      return (
        <>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={radius}
            {...commonRect}
          />
          <rect
            x={x}
            y={y}
            width={6}
            height={height}
            rx={3}
            fill={
              referenceResolution?.status === "missing" ? "#dc2626" : "#6366f1"
            }
          />
          <foreignObject
            x={x + 18}
            y={y + 12}
            width={width - 30}
            height={height - 24}
          >
            <button
              type="button"
              disabled={onReferenceActivate === undefined}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onReferenceActivate?.(node)}
              style={{
                width: "100%",
                height: "100%",
                padding: 0,
                border: 0,
                color: textColor,
                background: "transparent",
                textAlign: "left",
                cursor:
                  onReferenceActivate === undefined ? "default" : "pointer",
              }}
            >
              <strong
                style={{
                  display: "block",
                  fontSize,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </strong>
              {summary !== undefined && (
                <span
                  style={{
                    display: "-webkit-box",
                    marginTop: 8,
                    color: "#64748b",
                    fontSize: Math.max(12, fontSize - 2),
                    lineHeight: 1.4,
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {summary}
                </span>
              )}
            </button>
          </foreignObject>
        </>
      );
    }
    case "text":
      return (
        <foreignObject x={x} y={y} width={width} height={height}>
          <div
            style={{
              width: "100%",
              height: "100%",
              color: textColor,
              fontSize, lineHeight: 1.45, fontFamily,
              ...canvasTextStyle(node),
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
              overflow: "hidden",
            }}
          >
            {canvasStringProp(node.props, "text", "")}
          </div>
        </foreignObject>
      );
    case "sticky":
      return (
        <>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={radius}
            {...commonRect}
          />
          <foreignObject
            x={x + 14}
            y={y + 14}
            width={width - 28}
            height={height - 28}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                color: textColor,
                fontSize, lineHeight: 1.45, fontFamily,
                ...canvasTextStyle(node),
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
                overflow: "hidden",
              }}
            >
              {canvasStringProp(node.props, "text", "")}
            </div>
          </foreignObject>
        </>
      );
    default:
      return (
        <>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={radius}
            {...commonRect}
            strokeDasharray="6 5"
          />
          <text x={x + 12} y={y + 26} fill="#64748b" fontSize={13}>
            {messages.unknownNode(node.type)}
          </text>
        </>
      );
  }
}
